import { ListItemButton, ListItemIcon, ListItemText, Theme, Tooltip } from '@mui/material';
import { ReactElement, ReactNode } from 'react';
import { makeStyles } from '@mui/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface CollapseButtonProps {
    text: string | ReactNode;
    inset?: boolean;
    onClick?: () => void;
    drawerCollapsed?: boolean;
    isActive?: boolean;
    suffix?: ReactNode;
    tooltip?: string | ReactNode;
    isCollapsed: boolean;
    width?: number;
}

const useStyles = makeStyles((theme: Theme) => ({
    iconBase: {
        fontSize: theme.sidebar.iconSize,
        color: theme.sidebar.palette.activeLink,
        height: 24,
        verticalAlign: 'middle',

        '& svg': {
            fontSize: theme.sidebar.iconSize,
        },
    },
    fontIcon: {
        marginTop: -4,
        lineHeight: 'unset',
    },
    textBase: {
        fontSize: theme.sidebar.linkItemSize,
        color: theme.palette.grey[700],
    },
    inactive: {
        color: theme.sidebar.palette.text,
    },
    gutters: {
        paddingLeft: (theme.sidebar.widthCollapsed - theme.sidebar.iconSize) / 2,
    },
    rootListItem: (props: CollapseButtonProps) => ({
        '&:hover': {
            backgroundColor: theme.palette.grey[200],
        },
        width: props.width ?? 460,
        transition: 'width 0.3 ease-out; borderRadius 0.7s ease-out',
        backgroundColor: theme.general.colors.activitySidebarBackground,
        borderRadius: 0,
        borderLeft: '1px solid #dcdcdc',

        ...(props.isCollapsed && {
            width: 30,
            transition: 'borderRadius 0.7s',
            borderRadius: '26px 0 0 26px',
            backgroundColor: theme.palette.grey[300],
        }),
    }),
    placing: {
        position: 'fixed',
        bottom: 16,
        right: 0,
    },
}));

export const CollapseButton = (props: CollapseButtonProps): ReactElement => {
    const classes = useStyles(props);

    const textStyle = `${classes.textBase} ${!props.isActive ? classes.inactive : ''}`;
    const listItemStyle = `${classes.rootListItem} `;
    const iconStyle = `${classes.iconBase} ${!props.isActive ? classes.inactive : ''}`;

    return (
        <div className={classes.placing}>
            <Tooltip title={props.isCollapsed ? props.tooltip : ''} placement="top">
                <div>
                    <ListItemButton
                        onClick={props.onClick}
                        classes={{
                            root: listItemStyle,
                            gutters: classes.gutters,
                        }}
                        disableGutters={false}
                        sx={{
                            '&& .MuiTouchRipple-child': {
                                color: theme => theme.sidebar.palette.text,
                            },
                        }}
                    >
                        {props.text && !props.isCollapsed && (
                            <ListItemText primary={props.text} inset={props.inset} classes={{ primary: textStyle }} />
                        )}
                        <ListItemIcon className={iconStyle}>
                            {props.isCollapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                        </ListItemIcon>
                    </ListItemButton>
                </div>
            </Tooltip>
        </div>
    );
};
